import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError, request } from '../lib/api.js';

/**
 * Creating a customer.
 *
 * A route rather than a dialog, deliberately. The customer list states the rule this screen
 * follows — nothing lives in `useState` that a colleague might need to see — and a creation
 * form is the one place that is *almost* untrue: the half-filled form is genuinely local. What
 * is not local is being here at all, and `/customers/new` means the back button works, a
 * reload does not trap anybody in a modal, and "open a new customer" is a link somebody can be
 * sent.
 *
 * Three decisions worth stating.
 *
 * **Contact details are part of creation, not a follow-up.** The receptionist has the phone
 * number and the address on the call; making them save a bare name and then add three things
 * to it turns one act into four, and the API takes all of it in a single transaction so a
 * half-finished customer cannot exist.
 *
 * **The server owns validation.** This form checks only what it can check honestly — is there
 * a name, is there at least one digit in a phone number — and shows whatever the API says
 * about the rest. Restating the API's rules here would mean two copies that drift, and the
 * copy the user sees would be the one that is wrong.
 *
 * **`Idempotency-Key` is minted once per form, not per submit.** A coordinator who presses
 * Save twice, or whose connection stalls and who presses it again, must not end up with two
 * identical customers — and this is exactly the client the API's replay support was written
 * for.
 */

type Draft = {
  firstName: string;
  lastName: string;
  organization: string;
  locale: 'fr' | 'en';
  note: string;
  taxable: boolean;
  phones: Array<{ kind: string; raw: string; extension: string }>;
  emails: Array<{ kind: string; email: string }>;
  address: { line1: string; line2: string; city: string; region: string; postalCode: string; note: string };
};

const EMPTY: Draft = {
  firstName: '',
  lastName: '',
  organization: '',
  locale: 'fr',
  note: '',
  taxable: true,
  // One of each, present from the start: an empty repeater with an "add" link reads as
  // optional, and 97% of these customers have a phone number.
  phones: [{ kind: 'mobile', raw: '', extension: '' }],
  emails: [{ kind: 'main', email: '' }],
  address: { line1: '', line2: '', city: '', region: 'QC', postalCode: '', note: '' },
};

/** Matches `@monrdv/shared`'s `normalizePhone`: a number with no digits is not a number. */
const hasDigits = (value: string) => /\d/.test(value);

const PHONE_KINDS = ['mobile', 'home', 'work', 'main', 'other'] as const;
const EMAIL_KINDS = ['main', 'personal', 'professional', 'billing', 'other'] as const;

export function CustomerNewScreen() {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const navigate = useNavigate();
  const client = useQueryClient();

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  /*
   * One key for the life of this form. Regenerated only when a submission is *refused* on its
   * merits, because the next attempt is then a genuinely different request; a network failure
   * keeps the key, which is the whole point.
   */
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const named = !!(draft.firstName.trim() || draft.lastName.trim() || draft.organization.trim());
  const phonesValid = draft.phones.every((p) => !p.raw.trim() || hasDigits(p.raw));

  const create = useMutation({
    mutationFn: () => {
      // Only what was filled in travels. Sending empty strings would store them as data — a
      // customer whose organisation is "" reads differently from one who has none.
      const trimmed = (value: string) => (value.trim() ? value.trim() : undefined);
      const phones = draft.phones
        .filter((p) => p.raw.trim())
        .map((p, index) => ({
          kind: p.kind,
          raw: p.raw.trim(),
          extension: trimmed(p.extension),
          isPrimary: index === 0,
        }));
      const emails = draft.emails
        .filter((e) => e.email.trim())
        .map((e, index) => ({ kind: e.kind, email: e.email.trim(), isPrimary: index === 0 }));
      const address = draft.address.line1.trim() && draft.address.city.trim()
        ? {
            line1: draft.address.line1.trim(),
            line2: trimmed(draft.address.line2),
            city: draft.address.city.trim(),
            region: draft.address.region.trim() || 'QC',
            postalCode: trimmed(draft.address.postalCode),
            note: trimmed(draft.address.note),
            isPrimary: true,
          }
        : undefined;

      return request<{ id: string }>('/customers', {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify({
          firstName: trimmed(draft.firstName),
          lastName: trimmed(draft.lastName),
          organization: trimmed(draft.organization),
          locale: draft.locale,
          note: trimmed(draft.note),
          taxable: draft.taxable,
          phones,
          emails,
          address,
        }),
      });
    },
    onSuccess: (created) => {
      // The directory, the search index and every count that includes customers are now wrong.
      void client.invalidateQueries({ queryKey: ['customers'] });
      void client.invalidateQueries({ queryKey: ['search'] });
      // Straight to the new file: the reason for creating a customer is almost always to do
      // something with them next.
      void navigate({ to: '/customers/$customerId', params: { customerId: created.id } });
    },
    onError: (err) => {
      setError(err instanceof ApiError && err.message ? err.message : t('customer.form.failed'));
      // A refusal on the merits means the next attempt is a different request. Without a fresh
      // key the API would replay this same 4xx forever, and the form could never be corrected.
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        setIdempotencyKey(crypto.randomUUID());
      }
    },
  });

  const busy = create.isPending;

  return (
    <form
      className="recform"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (named && phonesValid) create.mutate();
      }}
      noValidate
    >
      <header className="recform__head">
        <h1 className="recform__title">{t('customer.new')}</h1>
        <p className="recform__lede">{t('customer.form.lede')}</p>
      </header>

      {error && (
        <p className="recform__error" role="alert">
          {error}
        </p>
      )}

      <fieldset className="recform__section" disabled={busy}>
        <legend className="recform__legend">{t('customer.form.identity')}</legend>

        <div className="recform__pair">
          <div className="recform__field">
            <label className="recform__label" htmlFor="firstName">
              {t('customer.form.firstName')}
            </label>
            <input
              id="firstName"
              className="recform__input"
              value={draft.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              autoComplete="off"
              autoFocus
            />
          </div>
          <div className="recform__field">
            <label className="recform__label" htmlFor="lastName">
              {t('customer.form.lastName')}
            </label>
            <input
              id="lastName"
              className="recform__input"
              value={draft.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="recform__field">
          <label className="recform__label" htmlFor="organization">
            {t('customer.form.organization')}
          </label>
          <input
            id="organization"
            className="recform__input"
            value={draft.organization}
            onChange={(e) => set('organization', e.target.value)}
            autoComplete="off"
          />
          {/* Says the actual rule rather than marking three fields required, none of which is. */}
          <span className="recform__hint">{t('customer.form.nameRule')}</span>
        </div>

        <div className="recform__pair">
          <div className="recform__field">
            <label className="recform__label" htmlFor="locale">
              {t('customer.form.locale')}
            </label>
            <select
              id="locale"
              className="recform__select"
              value={draft.locale}
              onChange={(e) => set('locale', e.target.value as 'fr' | 'en')}
            >
              <option value="fr">{t('customer.form.localeFr')}</option>
              <option value="en">{t('customer.form.localeEn')}</option>
            </select>
            {/* The customer's language, not the coordinator's. Their invoices follow it. */}
            <span className="recform__hint">{t('customer.form.localeHint')}</span>
          </div>
          <div className="recform__field">
            <label className="recform__label">{t('customer.form.billing')}</label>
            <label className="recform__check">
              <input
                type="checkbox"
                checked={draft.taxable}
                onChange={(e) => set('taxable', e.target.checked)}
              />
              <span>{t('customer.form.taxable')}</span>
            </label>
          </div>
        </div>
      </fieldset>

      <fieldset className="recform__section" disabled={busy}>
        <legend className="recform__legend">{t('customer.form.contact')}</legend>

        <div className="recform__repeat">
          {draft.phones.map((phone, index) => {
            const invalid = !!phone.raw.trim() && !hasDigits(phone.raw);
            return (
              <div className="recform__row" key={`phone-${index}`}>
                <select
                  className="recform__select"
                  aria-label={t('customer.form.phoneKind')}
                  value={phone.kind}
                  onChange={(e) => {
                    const phones = [...draft.phones];
                    phones[index] = { ...phone, kind: e.target.value };
                    set('phones', phones);
                  }}
                >
                  {PHONE_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {t(`customer.form.kind.${kind}`)}
                    </option>
                  ))}
                </select>
                <input
                  className={`recform__input recform__input--tel${invalid ? ' recform__input--invalid' : ''}`}
                  aria-label={`${t('customer.phone')} ${index + 1}`}
                  aria-invalid={invalid || undefined}
                  inputMode="tel"
                  autoComplete="off"
                  placeholder={t('customer.form.phonePlaceholder')}
                  value={phone.raw}
                  onChange={(e) => {
                    const phones = [...draft.phones];
                    phones[index] = { ...phone, raw: e.target.value };
                    set('phones', phones);
                  }}
                />
                <RemoveButton
                  label={t('customer.form.removePhone')}
                  // The last row is cleared rather than removed: a repeater that can empty
                  // itself leaves nothing to type into and no obvious way back.
                  onClick={() =>
                    set(
                      'phones',
                      draft.phones.length > 1
                        ? draft.phones.filter((_, i) => i !== index)
                        : [{ kind: 'mobile', raw: '', extension: '' }],
                    )
                  }
                />
              </div>
            );
          })}
          <button
            type="button"
            className="recform__add"
            disabled={draft.phones.length >= 10}
            onClick={() => set('phones', [...draft.phones, { kind: 'home', raw: '', extension: '' }])}
          >
            {t('customer.form.addPhone')}
          </button>
        </div>

        <div className="recform__repeat">
          {draft.emails.map((entry, index) => (
            <div className="recform__row" key={`email-${index}`}>
              <select
                className="recform__select"
                aria-label={t('customer.form.emailKind')}
                value={entry.kind}
                onChange={(e) => {
                  const emails = [...draft.emails];
                  emails[index] = { ...entry, kind: e.target.value };
                  set('emails', emails);
                }}
              >
                {EMAIL_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`customer.form.kind.${kind}`)}
                  </option>
                ))}
              </select>
              <input
                className="recform__input"
                aria-label={`${t('customer.email')} ${index + 1}`}
                type="email"
                inputMode="email"
                autoComplete="off"
                placeholder={t('customer.form.emailPlaceholder')}
                value={entry.email}
                onChange={(e) => {
                  const emails = [...draft.emails];
                  emails[index] = { ...entry, email: e.target.value };
                  set('emails', emails);
                }}
              />
              <RemoveButton
                label={t('customer.form.removeEmail')}
                onClick={() =>
                  set(
                    'emails',
                    draft.emails.length > 1
                      ? draft.emails.filter((_, i) => i !== index)
                      : [{ kind: 'main', email: '' }],
                  )
                }
              />
            </div>
          ))}
          <button
            type="button"
            className="recform__add"
            disabled={draft.emails.length >= 10}
            onClick={() => set('emails', [...draft.emails, { kind: 'personal', email: '' }])}
          >
            {t('customer.form.addEmail')}
          </button>
        </div>
      </fieldset>

      <fieldset className="recform__section" disabled={busy}>
        <legend className="recform__legend">{t('customer.form.serviceAddress')}</legend>

        <div className="recform__field">
          <label className="recform__label" htmlFor="line1">
            {t('customer.form.line1')}
          </label>
          <input
            id="line1"
            className="recform__input"
            value={draft.address.line1}
            onChange={(e) => set('address', { ...draft.address, line1: e.target.value })}
            autoComplete="off"
            placeholder={t('customer.form.line1Placeholder')}
          />
          {/* The unit is parsed out of this line by the database, so it belongs in it. */}
          <span className="recform__hint">{t('customer.form.line1Hint')}</span>
        </div>

        <div className="recform__field">
          <label className="recform__label" htmlFor="line2">
            {t('customer.form.line2')} <span className="recform__optional">{t('common.optional')}</span>
          </label>
          <input
            id="line2"
            className="recform__input"
            value={draft.address.line2}
            onChange={(e) => set('address', { ...draft.address, line2: e.target.value })}
            autoComplete="off"
          />
        </div>

        <div className="recform__pair">
          <div className="recform__field">
            <label className="recform__label" htmlFor="city">
              {t('customer.city')}
            </label>
            <input
              id="city"
              className="recform__input"
              value={draft.address.city}
              onChange={(e) => set('address', { ...draft.address, city: e.target.value })}
              autoComplete="off"
            />
          </div>
          <div className="recform__field">
            <label className="recform__label" htmlFor="postalCode">
              {t('customer.form.postalCode')} <span className="recform__optional">{t('common.optional')}</span>
            </label>
            <input
              id="postalCode"
              className="recform__input"
              value={draft.address.postalCode}
              onChange={(e) => set('address', { ...draft.address, postalCode: e.target.value })}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="recform__field">
          <label className="recform__label" htmlFor="accessNote">
            {t('customer.form.accessNote')} <span className="recform__optional">{t('common.optional')}</span>
          </label>
          <input
            id="accessNote"
            className="recform__input"
            value={draft.address.note}
            onChange={(e) => set('address', { ...draft.address, note: e.target.value })}
            placeholder={t('customer.form.accessNotePlaceholder')}
            autoComplete="off"
          />
          {/* The single most re-read field in the field app: gate codes, where to park. */}
          <span className="recform__hint">{t('customer.form.accessNoteHint')}</span>
        </div>
      </fieldset>

      <fieldset className="recform__section" disabled={busy}>
        <legend className="recform__legend">{t('customer.internalNote')}</legend>
        <div className="recform__field">
          <label className="recform__label" htmlFor="note">
            {t('customer.internalNote')} <span className="recform__optional">{t('common.optional')}</span>
          </label>
          <textarea
            id="note"
            className="recform__textarea"
            value={draft.note}
            onChange={(e) => set('note', e.target.value)}
          />
          <span className="recform__hint">{t('customer.form.noteHint')}</span>
        </div>
      </fieldset>

      <div className="recform__actions">
        <button className="btn btn--primary" type="submit" disabled={busy || !named || !phonesValid}>
          {busy ? t('customer.form.saving') : t('common.save')}
        </button>
        <button className="btn btn--ghost" type="button" disabled={busy} onClick={() => void navigate({ to: '/customers' })}>
          {t('common.cancel')}
        </button>
        <span className="recform__spacer" />
      </div>
    </form>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="recform__remove" aria-label={label} title={label} onClick={onClick}>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2.5 6h7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </button>
  );
}
